import { ReactNode } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Tab,
  TabList,
  makeStyles,
  tokens,
  SelectTabData,
  SelectTabEvent,
} from '@fluentui/react-components'
import {
  AddCircle24Regular,
  DocumentQueue24Regular,
  ShieldTask24Regular,
} from '@fluentui/react-icons'
import { useConfig } from '../context/ConfigContext'
import { QueueStatus } from './QueueStatus'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 20px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  logoImage: {
    height: '32px',
    width: 'auto',
  },
  logoText: {
    fontSize: '18px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  nav: {
    padding: '0 20px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  tabIcon: {
    marginRight: '8px',
  },
  content: {
    flex: 1,
    padding: '20px',
    overflowY: 'auto',
  },
})

interface LayoutProps {
  children: ReactNode
}

export function Layout({ children }: LayoutProps) {
  const styles = useStyles()
  const navigate = useNavigate()
  const location = useLocation()
  const { config } = useConfig()

  const currentTab = location.pathname.startsWith('/admin')
    ? 'admin'
    : location.pathname.startsWith('/tickets')
      ? 'tickets'
      : 'submit'

  const handleTabSelect = (_: SelectTabEvent, data: SelectTabData) => {
    if (data.value === 'submit') {
      navigate('/submit')
    } else if (data.value === 'tickets') {
      navigate('/tickets')
    } else if (data.value === 'admin') {
      navigate('/admin')
    }
  }

  return (
    <div className={styles.root}>
      <header className={styles.header}>
        <div className={styles.logo}>
          {config?.tenant.logoUrl && (
            // The Teams app is Vite-based and does not provide next/image.
            // eslint-disable-next-line @next/next/no-img-element
            <img 
              src={config.tenant.logoUrl} 
              alt={config.tenant.name} 
              className={styles.logoImage}
            />
          )}
          <span className={styles.logoText}>
            {config?.tenant.name || 'Support Portal'}
          </span>
        </div>
        <QueueStatus />
      </header>

      <nav className={styles.nav}>
        <TabList 
          selectedValue={currentTab} 
          onTabSelect={handleTabSelect}
          size="large"
        >
          <Tab value="submit" icon={<AddCircle24Regular />}>
            Submit Request
          </Tab>
          <Tab value="tickets" icon={<DocumentQueue24Regular />}>
            My Tickets
          </Tab>
          <Tab value="admin" icon={<ShieldTask24Regular />}>
            Admin Dashboard
          </Tab>
        </TabList>
      </nav>

      <main className={styles.content}>
        {children}
      </main>
    </div>
  )
}
